import { PriceFunc, PriceElemsData, PriceSegment, price } from '@icc/price';
import { Injectable, Inject } from '@angular/core';
import { CouplingActive } from '@icc/window-door/types';
import {APP_CONFIG, AppConfig, AppConfigFactory} from '@icc/shared/config';;
import { ProfilesPriceService } from '@icc/construction-elements/configurator';
import { Common } from '@icc/shared/helpers';


@Injectable()
export class PriceCoupledWindowService {
    constructor(
        @Inject(APP_CONFIG) private config: AppConfigFactory,
        private profilesPriceService: ProfilesPriceService
    ) {}

    /**
     * Wylicza dopłatę procentową za zmianę koloru uszczelki.
     *
     * @param {number} price         Wejsciowa cena
     * @param {object} PriceElems    Składowe wyceny
     * @param {array}  NoPriceCauses Powody braku wyceny
     * @param {array}  constrElems   Lista elementów konstrukcyjnych, do przeliczenia przez dopłatę.
     * @param {object} sealColor     Okucie bezpieczne
     * @return {number} Cena z doliczoną dopłatą.
     */
    @PriceFunc({
        shortName: 'coupledWindow',
        data: {
            windows: 'conf.windows',
            rollerShutters: 'conf.rollerShutters',
        },
    })
    suppCoupledWindow(
        {  }: PriceElemsData,
        { windows, rollerShutters }: { windows; rollerShutters }
    ): PriceSegment[] {
        const priceStack: PriceSegment[] = [];
        let constructionIndex = 1;
        for (const window of windows) {
            // eslint-disable-next-line object-shorthand, no-loop-func
            priceStack.push(...window.details.priceSegmentsNoMargin.map(x => ({...x, baseValue: x.value, skip: true, data: {...x.data,  constructionIndex}})));
            constructionIndex++;
        }

        for (const rollerShutter of rollerShutters) {
            // eslint-disable-next-line object-shorthand, no-loop-func
            priceStack.push(...rollerShutter.details.priceSegmentsNoMargin.map(x => ({...x, baseValue: x.value, skip: true, data: {...x.data, constructionIndex}})));
            constructionIndex++;

        }

        return priceStack;
    }

    /**
     * Dolicza doplatę za łączniki
     * @param  {number} price       Cena wejściowa
     * @param  {object} PriceElems  Wycena
     * @param  {object} conf        Konstrukcja
     * @param  {object} prices      Ceny slupkow
     * @param  {object} colors      Kolory
     * @return {number}             Cena po dopłacie
     */
    @PriceFunc({
        shortName: 'coupled-couplings',
        data: {
            drawData: 'conf.drawData',
            prices: 'data.profilesPrices',
            profiles: 'data.profiles',
            couplings: 'conf.couplings',
            reinforcements: 'data.reinforcements',
            alushells: 'data.alushells',
            alushellPrices: 'data.profilesAlushellPrices',
            customPrice: 'price.Profile',
            usedProfiles: 'conf.usedProfiles',
        },
    })
    suppCouplings(
        {  }: PriceElemsData,
        {
            drawData,
            prices,
            profiles,
            couplings,
            reinforcements,
            alushells,
            alushellPrices,
            customPrice,
            usedProfiles,

        }: {
            drawData: any;
            prices;
            profiles;
            couplings: CouplingActive[];
            reinforcements;
            alushells;
            alushellPrices;
            customPrice;
            usedProfiles;
        }
    ): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];

        if (couplings.length) {
            couplings.forEach((coupling, index) => {
                const couplingData = drawData.coupling.find(o => o.couplingId === coupling.id);
                const couplingProfile = profiles?.find(o => o.id === coupling.profileId);
                const clonedCouplingProfile = profiles?.find(o => Number(o.originalProfile) === Number(coupling.profileId))
                    || profiles?.find(o => Number(o._clonedFrom) === Number(coupling.profileId));
                const foundUsedProfile = usedProfiles?.find(o => Number(o.id) === Number(coupling.profileId));
                
                const directClonedProfile = profiles?.find(o => Number(o.id) === Number(coupling.profileId) && (o as any)._clonedFrom);
                const effectiveCouplingProfile = clonedCouplingProfile || couplingProfile || directClonedProfile || foundUsedProfile;
                const effectiveProfileId = effectiveCouplingProfile?.id ?? coupling.profileId;
                const couplingProfileWithWidth = effectiveCouplingProfile
                    ? { ...effectiveCouplingProfile, selectedWidth: coupling.widthOut, widthOut: coupling.widthOut }
                    : undefined;

                const profilePrice = this.profilesPriceService.getProfilePrice(
                    effectiveProfileId,
                    'coupling',
                    null,
                    coupling.color,
                    prices,
                    customPrice,
                    undefined,
                    undefined,
                    undefined,
                    coupling.widthOut,
                    couplingProfileWithWidth

                );
                const reinforcement = reinforcements
                    ? reinforcements.find(
                          o =>
                              coupling.reinforcement
                              && Number(o.id) === Number(coupling.reinforcement.id)
                      )
                    : null;

                let length = coupling.length / 1000;
                if (
                    couplingData
                    && this.config().IccConfig.Configurators.price.mullionsLength === 'real'
                ) {
                    const couplingLength = Number(couplingData.length) || 0;
                    length = couplingLength / 1000;
                }

                if (profilePrice) {
                    const pricePiece = parseFloat(profilePrice.price_piece);
                    const priceLength = parseFloat(profilePrice.price_length);

                    priceSegments.push({
                        type: 'couplings',
                        baseValue: price(pricePiece),
                        value: price(pricePiece),
                        valueType: 'value',
                        data: {
                            profileId: coupling.profileId,
                            couplingId: coupling.id,
                            priceLevelId: effectiveCouplingProfile?.priceLevelId ?? couplingProfile?.priceLevelId,
                            length,
                        },
                    });
                    priceSegments.push({
                        type: 'couplings',
                        baseValue: price(priceLength, length),
                        value: price(priceLength, length),
                        valueType: 'value',
                        data: {
                            profileId: coupling.profileId,
                            couplingId: coupling.id,
                            priceLevelId: effectiveCouplingProfile?.priceLevelId ?? couplingProfile?.priceLevelId,
                            length,
                        },
                    });
                    if (effectiveCouplingProfile) {
                        let reinforcementLength = length * 1000;
                        if (couplingProfile && reinforcement) {
                            reinforcementLength =
                                length * 1000
                                - effectiveCouplingProfile.spaceReinforcement * 2
                                - reinforcement.length_correction * 2;
                        }
                        if (reinforcement && reinforcement.length_step) {
                            reinforcementLength =
                                Math.floor(reinforcementLength / reinforcement.length_step)
                                * reinforcement.length_step;
                        }
                        if(reinforcement) {
                           priceSegments.push({
                                type: 'couplings',
                                baseValue: price(reinforcement.price, reinforcementLength / 1000),
                                value: price(reinforcement.price, reinforcementLength / 1000),
                                valueType: 'value',
                                data: {
                                    profileId: coupling.profileId,
                                    couplingId: coupling.id,
                                    priceLevelId: effectiveCouplingProfile?.priceLevelId ?? couplingProfile?.priceLevelId,
                                    length: reinforcementLength / 1000,
                                },
                            }); 
                        }
                        
                    }
                }
                if (!profilePrice && ( reinforcement?.price === undefined || reinforcement?.price === null)) {
                    priceSegments.push({
                        type: 'couplings',
                        baseValue: null,
                        value: null,
                        valueType: 'value',
                        data: {
                            profileId: coupling.profileId,
                            couplingId: coupling.id,
                            priceLevelId: effectiveCouplingProfile?.priceLevelId ?? couplingProfile?.priceLevelId,
                            length,
                        },
                    });
                }
            });
        }

        return priceSegments;
    }

    @PriceFunc({
        shortName: 'coupled-sideProfiles',
        data: {
            drawData: 'conf.drawData',
            sideProfiles: 'conf.sideProfiles',
            prices: 'data.profilesPrices',
            allProfiles: 'data.profiles',
            customPrice: 'price.Profile',
        },
    })
    suppSideProfiles(
        { PriceElems, NoPriceCauses }: PriceElemsData,
        { drawData, sideProfiles, prices, allProfiles, customPrice }
    ): PriceSegment[] {
        const priceSegments: PriceSegment[] = [];
        if (Common.isObject(prices) && Common.isArray(sideProfiles)) {
            sideProfiles.forEach((sideProfile, index) => {
                const profileDimensions = drawData.extension.find(
                    o => o.extensionId === sideProfile.id
                );
                const profile = allProfiles.find(o => o.id === sideProfile.profileId);
                const profilePrice = this.profilesPriceService.getProfilePrice(
                    sideProfile.profileId,
                    profile ? profile.type : null,
                    null,
                    sideProfile.color,
                    prices,
                    customPrice
                );
                if (profilePrice) {
                    priceSegments.push({
                        type: 'sideProfile',
                        baseValue: price(profilePrice.price_piece),
                        value: price(profilePrice.price_piece),
                        valueType: 'value',
                        data: {
                            profileId: sideProfile.profileId,
                            priceLevelId: profile.priceLevelId,
                            id: sideProfile.id,
                        },
                    });
                    if (profileDimensions) {
                        priceSegments.push({
                            type: 'sideProfile',
                            baseValue:
                                price(profilePrice.price_length, profileDimensions.length / 1000),
                            value:
                                price(profilePrice.price_length, profileDimensions.length / 1000),
                            valueType: 'value',
                            data: {
                                profileId: sideProfile.profileId,
                                priceLevelId: profile.priceLevelId,
                                id: sideProfile.id,
                            },
                        });
                        if (profilePrice.price_area) {
                            priceSegments.push({
                                type: 'sideProfile',
                                baseValue: price(profilePrice.price_area, profileDimensions.polyArea),
                                value: price(profilePrice.price_area, profileDimensions.polyArea),
                                valueType: 'value',
                                data: {
                                    profileId: sideProfile.profileId,
                                    priceLevelId: profile.priceLevelId,
                                    id: sideProfile.id,
                                },
                            });
                        }
                        if (sideProfile.reinforcement?.id) {
                            priceSegments.push({
                                type: 'sideProfile',
                                baseValue: price(
                                    sideProfile.reinforcement.price,
                                    profileDimensions.length / 1000
                                ),
                                value: price(
                                    sideProfile.reinforcement.price,
                                    profileDimensions.length / 1000
                                ),
                                valueType: 'value',
                                data: {
                                    id: sideProfile.id,
                                    profileId: sideProfile.profileId,
                                    reinforcementId: sideProfile.reinforcement.id,
                                    length: profileDimensions.length / 1000,
                                },
                            });
                        }
                    }
                } else {
                    priceSegments.push({
                        type: 'sideProfile',
                        baseValue: null,
                        value: null,
                        valueType: 'value',
                        data: {},
                    });
                    NoPriceCauses.push('no prices for sideProfile');
                }
            });
        } else {
            priceSegments.push({
                type: 'sideProfile',
                baseValue: null,
                value: null,
                valueType: 'value',
                data: {},
            });
            NoPriceCauses.push('no prices for sideProfile');
        }
        return priceSegments;
    }
}
